/**
 * Where a mission's sun (or moon) hangs, and which `SUN.BMP` disc is drawn
 * there. What colour the sky and ground take at that hour is `sky-run.ts`,
 * which this module re-exports at the bottom — the two are `sun.c`'s two
 * subjects and this file split along that seam when it outgrew its ceiling.
 *
 * The original's answer is in `s\game\sun.c`, recovered by static RE of
 * `RB.EXE` (evidence trail and disassembly in
 * `docs/engineering/time-of-day-design.md`). Every colour below is a
 * `BARON.PAL` entry — the sim's one and only in-flight palette. What is
 * *recovered* and what is *this port's choice* differs per item, so both are
 * marked. None of it is gated: this whole file is the `original` treatment
 * (`effects-settings.ts`'s `SKY_TREATMENTS`), and both treatments a player can
 * pick are hour-driven. Only the `measured` debug reference passes `treated:
 * false` and takes `MEASURED_LOOK` instead.
 *
 * ## AUTHENTIC — the original draws a sun, and a moon, from `SUN.BMP`
 *
 * `init.c` (`RB.EXE` file `0xdc0c`) loads `SUN.BMP` at startup and **asserts
 * that it loaded**, so it is not optional content. `sun.c`'s sky pass
 * (`0xfd9b`-`0x1008c`) blits one of its frames per sky object, centred on the
 * projected direction and clipped to the 3D viewport. `SUN.BMP` (`VOLUME.002`
 * offset 13275, 250 bytes, baked to `data/baked/sprites/SUN.BMP.png`) decodes
 * to **two frames, each one flat palette entry with a hard circular edge**:
 *
 * | frame | disc | `BARON.PAL` | colour | object |
 * |---|---|---|---|---|
 * | 0 | 15 x 15 px (in a 16-wide frame) | **200** | `#ffffa2` pale yellow | the sun |
 * | 1 | 17 x 17 px (in a 24-wide frame) | **199** | `#cbcbcb` light grey | the moon |
 *
 * Both are *single-colour* — 177 and 221 pixels of exactly one index, zero
 * shading, zero gradient. So there is **no glare, no halo and no lens flare** to
 * reproduce: the original's sun is a flat disc, and this module draws a flat
 * disc. (The manual's "the Sun Blind Spot will cause a glare" describes the
 * contact suppression, not a second sprite — no glare art and no palette-flash
 * code exists.) The frame 1 = moon reading is not a guess: `STARS.DAT` record 1
 * selects frame 1 and `sun.c` places it at the sun's angle **plus `0x8000`,
 * exactly 180 degrees opposite** (`0xfb54`+), which is a moon and nothing else.
 *
 * `SUN.BMP` is blitted at **1:1 pixels** with no perspective scaling
 * (`models-tbl.md` §4.3, and the sky pass confirms it — the blit takes the
 * frame's own width and height), so the disc's *angular* size is a property of
 * the art: 15 of 200 scanlines. `three/sprite-screen-size.ts` is that rule as
 * arithmetic and this module defers to it rather than restating it.
 *
 * ## AUTHENTIC — where the sun is, to the hour
 *
 * `sun.c` at `0xfb54` sets the sun's hour angle to `hour * 0x0AAB + 0xC000` in
 * a 16-bit turn (`0x10000` = 360 degrees), i.e. **`0x0AAB` = 2731 = one
 * twenty-fourth of a turn per hour, 15 degrees an hour**, offset by `0xC000` =
 * −90 degrees. Its azimuth is `STARS.DAT` record 0's own field. That law puts
 * the sun on the horizon at 06:00, overhead at noon, back on the horizon at
 * 18:00 and below it at midnight — and `sun.c`'s day/night switch at `0xf9f5`
 * is literally `sin(sunAngle) < 0`, which turns the moon and the stars on.
 *
 * `CELESTIAL_HOURS` below is the one invented number left in the chain: the
 * port's `MissionWeather.timeOfDay` is four buckets where the original had a
 * clock, so a bucket has to be given an hour to evaluate the law at.
 *
 * ## DISPROVEN — there are no time-of-day palette files
 *
 * The obvious 1990 mechanism would be a palette swap, and it is not what the
 * game does. All 35 shipped `.PAL` files were diffed against `BARON.PAL`
 * channel by channel: every one of the 34 others has `dR - dB < 0` (cooler than
 * `BARON.PAL`, never warmer, so none is a dawn/dusk tint), and none combines a
 * night-like scale factor with any correlation to it (`FIGHTER.PAL` is a
 * byte-identical duplicate; `PS.PAL` differs at 49 indices, none in the sky
 * ramp). `RB.EXE` holds exactly one `.pal` filename literal, `baron.pal`, and
 * zero occurrences of `dawn`, `dusk`, `night`, `sunrise` or `sunset`.
 */

import type { TimeOfDay, Vec3 } from "@red-opus/engine";
import type { PaletteColour } from "../world/terrain-palette.ts";

// One `BARON.PAL` colour, as the 24-bit value three.js takes. Declared in
// `world/terrain-palette.ts` — the module that is about the palette — and
// re-exported here because this file's own API is written in it.
export type { PaletteColour };

/**
 * A flat single-colour disc out of `SUN.BMP`, in the original's own units: the
 * frame's opaque pixel size against a 200-line framebuffer, and the palette
 * entry every one of its pixels holds.
 */
export interface CelestialDisc {
  readonly colour: PaletteColour;
  /** Opaque pixels across the frame, not the frame's padded width. */
  readonly widthPx: number;
  readonly heightPx: number;
}

/** `SUN.BMP` frame 0 — `BARON.PAL` 200, a 15 x 15 disc. */
export const SUN_DISC: CelestialDisc = { colour: 0xffffa2, widthPx: 15, heightPx: 15 };

/** `SUN.BMP` frame 1 — `BARON.PAL` 199, a 17 x 17 disc, placed opposite the sun. */
export const MOON_DISC: CelestialDisc = { colour: 0xcbcbcb, widthPx: 17, heightPx: 17 };

/** A turn in `sun.c`'s 16-bit angle units — `0x10000` is 360 degrees. */
const TURN = 0x10000;
/** `sun.c` `0xfb54`: `0x0AAB` per hour, one twenty-fourth of a turn. */
const HOUR_ANGLE_PER_HOUR = 0x0aab;
/** `sun.c` `0xfb54`: the `0xC000` the hour angle is offset by, which is −90 degrees. */
const HOUR_ANGLE_AT_MIDNIGHT = 0xc000;
/** `sun.c` `0xfb54`: the moon sits at the sun's angle plus half a turn. */
const MOON_ANGLE_OFFSET = 0x8000;

/** `STARS.DAT` record 0's azimuth field — the plane the sun's arc runs in. */
const SUN_AZIMUTH = 14000;
/** `STARS.DAT` record 1's azimuth field. */
const MOON_AZIMUTH = 18000;

/**
 * The hour each of the port's four buckets is evaluated at. INVENTED — the
 * original reads a clock (`ds:0x7254`) and `MissionWeather.timeOfDay` is four
 * words, so something has to bridge them. Noon and midnight are the only hours
 * `daytime` and `night` could mean. Dawn and dusk are placed half an hour
 * inside sunrise and sunset, which puts the sun about 7.5 degrees up — within
 * two degrees of the elevation this port's previous hand-picked sun direction
 * used, so the blind-spot cone behaves as it did before this law replaced it.
 */
export const CELESTIAL_HOURS: Readonly<Record<TimeOfDay, number>> = {
  dawn: 6.5,
  daytime: 12,
  dusk: 17.5,
  night: 0,
};

/**
 * `sun.c`'s hour angle for an hour, in turns. The sine of this is the body's
 * elevation, which is why `sin(angle) < 0` is the sim's own night test.
 */
export function hourAngleTurns(hour: number): number {
  return (((hour * HOUR_ANGLE_PER_HOUR + HOUR_ANGLE_AT_MIDNIGHT) % TURN) + TURN) % TURN / TURN;
}

/**
 * A sky object's direction in world axes (`math3d.ts`: `+X` east, `−Z` north,
 * `+Y` up) from the two angles `sun.c` keeps for it. Elevation is the sine of
 * the hour angle and the azimuth turns the remaining horizontal component about
 * the vertical — the mapping from the original's azimuth field onto compass
 * bearing is INFERRED (zero north, increasing east), since the handedness of
 * its own `sin/cos` pair was not recovered. It only rotates the arc's plane:
 * every elevation, and therefore the day/night switch and the overhead noon
 * sun, is unaffected by the choice.
 */
export function celestialDirection(angleTurns: number, azimuth: number): Vec3 {
  const angle = angleTurns * 2 * Math.PI;
  const bearing = (azimuth / TURN) * 2 * Math.PI;
  const horizontal = Math.cos(angle);
  return { x: horizontal * Math.sin(bearing), y: Math.sin(angle), z: -horizontal * Math.cos(bearing) };
}

/** The sun's direction at a time of day, or `undefined` once `sin(angle) < 0` puts it under the horizon. */
export function sunDirectionAt(timeOfDay: TimeOfDay | undefined): Vec3 | undefined {
  if (timeOfDay === undefined) return undefined;
  const direction = celestialDirection(hourAngleTurns(CELESTIAL_HOURS[timeOfDay]), SUN_AZIMUTH);
  return direction.y < 0 ? undefined : direction;
}

/** Which `SUN.BMP` frame hangs where, for a mission that briefs `timeOfDay`. */
export interface CelestialBody {
  readonly disc: CelestialDisc;
  readonly direction: Vec3;
}

/**
 * The sky object this mission draws. Not gated by the port-extension toggle:
 * the sprite is the original's own art, the original asserts on loading it, and
 * `sun.c`'s draw path never reads the SUN BLIND SPOT flag — the sun is drawn
 * whether or not that option is on. `undefined` only when the mission briefs no
 * weather at all (a dogfight or a duel), where there is no hour to place a body
 * at.
 *
 * Below the horizon the sun is replaced by the moon rather than by nothing,
 * which is `sun.c` `0xf9f5`'s own behaviour: `sin(sunAngle) < 0` hides object 0
 * and un-hides every other one.
 */
export function celestialBodyFor(timeOfDay: TimeOfDay | undefined): CelestialBody | undefined {
  if (timeOfDay === undefined) return undefined;
  const sunAngle = hourAngleTurns(CELESTIAL_HOURS[timeOfDay]);
  const sun = celestialDirection(sunAngle, SUN_AZIMUTH);
  if (sun.y >= 0) return { disc: SUN_DISC, direction: sun };
  const moonAngle = ((sunAngle + MOON_ANGLE_OFFSET / TURN) % 1 + 1) % 1;
  return { disc: MOON_DISC, direction: celestialDirection(moonAngle, MOON_AZIMUTH) };
}

// The sky's own colours moved to `sky-run.ts` when this file outgrew its size
// ceiling; the two halves are `sun.c`'s two subjects. Re-exported because this
// module's callers ask for "what does this hour look like" as one question.
export {
  DAYTIME_HAZE_CONCENTRATION,
  DAYTIME_SKY_RAMP,
  flatSkyRun,
  MEASURED_GROUND_GREEN,
  MEASURED_LOOK,
  MEASURED_SKY_BLUE,
  MEASURED_SKY_RUN,
  SKY_BAND_SCANLINES,
  SKY_RUN_EXTENT_DEG,
  skyBandCount,
  skyHorizon,
  skyLookFor,
  skyRunScanlines,
  skyZenith,
  TWILIGHT_CONCENTRATION,
  groundRampShiftFor,
  groundRampShiftForColour,
  type SkyLook,
  type SkyRun,
} from "./sky-run.ts";
